import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useIsLoomEntryPointsEnabledQuery$data,
	useIsLoomEntryPointsEnabledQuery$variables,
} from './__generated__/useIsLoomEntryPointsEnabledQuery.graphql.ts';
import { createAtlAttributionHeader } from './utils/createAtlAttributionHeader.ts';
import { isMeetingRecorderEligibilityEnabled } from './isMeetingRecorderEligibilityEnabled.ts';

const UseIsLoomEntryPointsEnabledQuery = graphql`
	query useIsLoomEntryPointsEnabledQuery(
		$cloudId: ID!
		$includeMeetingRecorderEligibility: Boolean!
	) {
		confluence_loomEntryPoints(cloudId: $cloudId) {
			isLoomEntryPointsEnabled
		}
		loom_settings(siteId: $cloudId) @include(if: $includeMeetingRecorderEligibility) {
			meetingRecordingsAvailable
			meetingNotesEligible
		}
		loom_connectedCalendars(siteId: $cloudId) @include(if: $includeMeetingRecorderEligibility) {
			guid
		}
		...loomPersonalization_entryPoints
	}
`;

export const useIsLoomEntryPointsEnabled = (): boolean | undefined => {
	const { accessStatus, activationId, cloudId } = useSessionData();
	const includeMeetingRecorderEligibility = isMeetingRecorderEligibilityEnabled();
	const shouldSkip =
		accessStatus !== AccessStatus.LICENSED_ADMIN_ACCESS &&
		accessStatus !== AccessStatus.LICENSED_USE_ACCESS;

	const { data, error, loading } = useQueryInterop<
		useIsLoomEntryPointsEnabledQuery$data,
		useIsLoomEntryPointsEnabledQuery$variables
	>(UseIsLoomEntryPointsEnabledQuery, {
		variables: {
			cloudId,
			includeMeetingRecorderEligibility,
		},
		context: {
			headers: {
				...createAtlAttributionHeader(cloudId, activationId),
			},
		},
		skip: shouldSkip || Boolean(window.__SSR_LOOM_ENTRY_POINT__),
	});

	if (window.__SSR_LOOM_ENTRY_POINT__) {
		return undefined;
	}

	if (error) {
		markErrorAsHandled(error);
		return true;
	}

	if (!loading && data) {
		return data?.confluence_loomEntryPoints?.isLoomEntryPointsEnabled;
	}
};
